/**
 * AdvocacySuite Component
 *
 * Grid of 4 module cards for quick navigation to main modules.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md Section 5.
 */

import { useNavigate } from 'react-router-dom';
import { Eye, FolderOpen, BookOpen, FileEdit, ChevronRight } from 'lucide-react';

interface ModuleCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  color: string;
}

interface AdvocacySuiteProps {
  activeCaseFileId?: string;
}

const modules: ModuleCard[] = [
  {
    id: 'watchtower',
    title: 'Watchtower',
    description: 'Monitor deadlines & risks',
    icon: Eye,
    path: '/watchtower',
    color: 'text-amber-600 bg-amber-50',
  },
  {
    id: 'casefile',
    title: 'CaseFile',
    description: 'Access records & forms',
    icon: FolderOpen,
    path: '/casefile',
    color: 'text-evergreen bg-evergreen/10',
  },
  {
    id: 'carelog',
    title: 'Care Log',
    description: "Log today's care notes",
    icon: BookOpen,
    path: '/care-log',
    color: 'text-blue-600 bg-blue-50',
  },
  {
    id: 'casemaker',
    title: 'CaseMaker',
    description: 'Draft letters & appeals',
    icon: FileEdit,
    path: '/casemaker',
    color: 'text-purple-600 bg-purple-50',
  },
];

export function AdvocacySuite({ activeCaseFileId }: AdvocacySuiteProps): JSX.Element {
  const navigate = useNavigate();

  const handleCardClick = (module: ModuleCard) => {
    // Navigate to module routes - all require activeCaseFileId
    if (module.id === 'casefile' && activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}`);
    } else if (module.id === 'carelog' && activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/carelogs`);
    } else if (module.id === 'watchtower' && activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/deadlines`);
    } else if (module.id === 'casemaker' && activeCaseFileId) {
      navigate(`/casefile/${activeCaseFileId}/casemaker`);
    } else if (!activeCaseFileId) {
      // No CaseFile selected - could show a toast or alert
      console.warn('Please select a care recipient first');
    } else {
      navigate(module.path);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-ink">Advocacy Suite</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {modules.map((module) => {
          const Icon = module.icon;

          return (
            <button
              key={module.id}
              onClick={() => handleCardClick(module)}
              className="card p-5 text-left group hover:shadow-card-hover transition-all duration-250"
            >
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-lg ${module.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <ChevronRight className="w-5 h-5 text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              <div className="mt-4">
                <h3 className="font-semibold text-ink">{module.title}</h3>
                <p className="text-sm text-muted mt-1">{module.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default AdvocacySuite;
